'use client';

import { Badge, BottomFixed, Button, IconDynamic, type IconName } from '@/components';
import { AnimatePresence, cn, motion } from '@/libs';
import { formatCurrency, parseHtml, ticketLabels, type UpsellOption } from '@/utils';

type UpSellProps = {
  amountTickets: number;
  totalValue: number;
  title: string;
  description: string;
  options: UpsellOption[];
  maxTicketsPerPurchase?: number;
  onAccept?: (option: UpsellOption) => void;
  onDecline?: () => void;
  isLoading?: boolean;
  'data-cy'?: string;
};

// TODO(ILOTTO-2034): order bump description is saved by admin but never rendered - decide whether to show it or drop the column
// TODO(ILOTTO-2034): this component and UpsellStep handle the DURING_ORDER offer, not the AFTER_ORDER upsell - rename both to order bump
export const UpSell = ({
  amountTickets,
  totalValue,
  title,
  description,
  options,
  maxTicketsPerPurchase,
  onAccept,
  onDecline,
  isLoading = false,
  'data-cy': dataCy,
}: UpSellProps) => {
  const isAtMax = maxTicketsPerPurchase != null && amountTickets >= maxTicketsPerPurchase;

  return (
    <div
      data-cy={dataCy || 'upsell'}
      className="flex flex-1 flex-col px-2.5 pt-2.5 w-full gap-2 pb-[150px] md:gap-4 lg:pb-0"
    >
      <h3
        className={cn(
          'text-foreground font-bold text-lg text-center',
          description && '[@media(max-height:680px)]:hidden',
        )}
      >
        Resumo da compra
      </h3>

      <div
        className={cn(
          'w-full flex flex-col gap-2 bg-background-variant p-3 rounded-lg border border-background',
          description && '[@media(max-height:680px)]:hidden',
        )}
      >
        <div className="w-full bg-background rounded-lg flex justify-between px-3 py-2">
          <span className="text-foreground text-sm">
            Quantidade de {ticketLabels.plural}:
          </span>
          <span
            data-cy="upsell-amount-tickets"
            className="text-foreground font-bold text-sm"
          >
            {amountTickets}
          </span>
        </div>
        <div className="w-full bg-background rounded-lg flex justify-between px-3 py-2 [@media(max-height:680px)]:hidden">
          <span className="text-foreground text-sm">Valor Total:</span>
          <span
            data-cy="upsell-total-value"
            className="text-foreground font-bold text-sm"
          >
            {formatCurrency(totalValue)}
          </span>
        </div>
      </div>

      <div className="w-full bg-background-variant border border-background rounded-lg">
        <div className="bg-background-variant relative p-3">
          <h4 className="text-foreground font-bold text-center">{title}</h4>
        </div>

        {description && (
          <div className="p-3 pt-3">
            <div className="text-foreground/80 text-sm text-center leading-relaxed">
              {parseHtml(description)}
            </div>
          </div>
        )}

        <div className="p-3 pt-0 space-y-4">
          {isAtMax ? (
            <p
              data-cy="upsell-max-reached-message"
              className="text-foreground/80 text-sm text-center leading-relaxed py-2"
            >
              {`Você já atingiu a quantidade máxima de ${maxTicketsPerPurchase} ${ticketLabels.plural} para este pedido.`}
            </p>
          ) : (
            <AnimatePresence>
              {options.map((option, index) => {
                const wouldExceed =
                  maxTicketsPerPurchase != null &&
                  amountTickets + option.quantity > maxTicketsPerPurchase;
                const isDisabled = wouldExceed || isLoading;

                return (
                  <motion.div
                    key={option.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.1,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <Button
                      data-cy={`upsell-option-${option.id}-button`}
                      size="large"
                      disabled={isDisabled}
                      onClick={() => {
                        if (wouldExceed) return;
                        onAccept?.(option);
                      }}
                      loading={isLoading}
                      className={cn(
                        'group relative w-full !h-12 text-foreground text-sm border bg-background hover:bg-foreground/10 border-primary/10 p-3 sm:hover:text-primary-foreground',
                        option.highlight &&
                          'animate-pulse duration-1000 transform scale-105 !border-1 !border-green-400 !bg-gradient-to-l from-green-400 to-green-500 text-background',
                        wouldExceed &&
                          'opacity-50 cursor-not-allowed !animate-none !transform-none',
                      )}
                    >
                      {option.percentage > 0 && (
                        <Badge
                          text={`${option.percentage}% OFF`}
                          className="absolute -top-3 -right-2 bg-badge text-foreground"
                        />
                      )}

                      <div className="flex items-center justify-between gap-2 w-full">
                        <div className="rounded-full aspect-square flex items-center justify-center h-7">
                          {IconDynamic(option.icon as IconName, {
                            width: 24,
                            height: 24,
                            className: cn(
                              'text-foreground sm:group-hover:text-primary-foreground [&_path]:fill-current',
                              option.highlight && 'text-background',
                            ),
                          })}
                        </div>
                        <span className="uppercase">{option.title}</span>
                        <div className="flex flex-col items-center">
                          <span
                            className={cn(
                              'font-medium text-xs uppercase text-foreground/50 sm:group-hover:text-primary-foreground/50 whitespace-nowrap',
                              option.highlight && 'text-background text-xs',
                            )}
                          >
                            {option.quantity === 1
                              ? `1 ${ticketLabels.singularCaps}`
                              : `${option.quantity} ${ticketLabels.pluralCaps}`}
                          </span>
                          <span className="font-bold">
                            +{formatCurrency(option.value)}
                          </span>
                        </div>
                      </div>
                    </Button>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </div>

      <BottomFixed
        hasFooter
        className="w-full mx-auto p-2 md:p-2.5 md:rounded-lg fixed bottom-[0px] z-50"
      >
        <BottomFixed.Header className="text-foreground flex flex-row items-center justify-between p-2">
          <span className="text-foreground text-xs font-medium uppercase">
            valor total
          </span>
          <span className="font-semibold text-secondary text-xl sm:text-2xl">
            {formatCurrency(totalValue)}
          </span>
        </BottomFixed.Header>

        <BottomFixed.Body className="flex flex-col rounded-lg flex-1 items-center gap-2">
          {onDecline && (
            <Button
              data-cy="upsell-decline-button"
              variant="tertiary"
              size="large"
              onClick={onDecline}
              disabled={isLoading}
              className="w-full h-12"
            >
              {isLoading ? 'Processando...' : 'Continuar sem adicionar'}
            </Button>
          )}
        </BottomFixed.Body>

        {/* <BottomFixed.Footer className="text-foreground flex flex-1 items-center justify-center flex-col gap-2">
          <p className="text-xs text-foreground">
            Resultado do sorteio através da <b>Loteria Federal</b>
          </p>
          <Image src={LoteriaFederalImage} alt="loteria-federal" width={60} height={13} />
        </BottomFixed.Footer> */}
      </BottomFixed>
    </div>
  );
};
